import React, { useState } from "react";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { selectRetentionDays } from "../../store/statusCountsSlice";
import { MarkerBand, RagMarker } from "./RagMarker";
import { noCompletionsText } from "./statusProgress";
import { MinActiveRowsForRag, RagGraceMinutes, RagGreenMinPercent, RagRedMaxPercent } from "./ragBand";

const LegendId = "status-legend";

const Wrapper = styled.span`
  position: relative;
  display: inline-flex;
`;

// Text button in the theme's primary blue (moodysBlueColor).
const TextButton = styled.button`
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  font-size: 14px;
  font-weight: 500;
  color: #0028a0;
  background: transparent;
  border: 0;
  border-radius: 5px;
  cursor: help;
  outline: none;

  &:hover {
    background: rgba(0, 40, 160, 0.07);
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(0, 40, 160, 0.45);
  }
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  width: 420px;
  background: #fff;
  color: #181d1f;
  border: 1px solid #babfc7;
  border-radius: 3px;
  box-shadow: 0 1px 4px 1px rgba(186, 191, 199, 0.4);
  font-size: 12.5px;
  line-height: 1.4;
  padding: 10px 12px 8px;

  h4 {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 700;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 2px 8px;
  padding: 5px 0;
  border-top: 1px solid #dde2eb;

  ul {
    grid-column: 2;
    margin: 2px 0 0;
    padding-left: 16px;
    color: #6c6c7b;
  }

  li {
    margin: 1px 0;
  }
`;

const Name = styled.span`
  font-weight: 600;
`;

const When = styled.span`
  grid-column: 2;
  color: #6c6c7b;
`;

const NoIconSwatch = styled.span`
  width: 17px;
  height: 17px;
  border: 1px dashed #b9b9c8;
  border-radius: 3px;
`;

const Bands: { band: MarkerBand; name: string; meaning: string; when: string }[] = [
  {
    band: "resolving",
    name: "Resolving",
    meaning: "data has just started arriving.",
    when: `At least 1 completed, first less than ${RagGraceMinutes} minutes ago.`,
  },
  {
    band: "red",
    name: "Red",
    meaning: "data arrived a while ago; most are still waiting.",
    when: `At least 1 completed, and ${RagRedMaxPercent}% or fewer.`,
  },
  {
    band: "amber",
    name: "Amber",
    meaning: "some completed, some still waiting.",
    when: `More than ${RagRedMaxPercent}% and less than ${RagGreenMinPercent}% completed.`,
  },
  {
    band: "green",
    name: "Green",
    meaning: "the data is there; this row looks out of step.",
    when: `${RagGreenMinPercent}% or more completed.`,
  },
];

const InfoIcon = () => (
  <svg viewBox="0 0 16 16" width={15} height={15} aria-hidden="true">
    <circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <path d="M8 7.2 V11.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
  </svg>
);

/**
 * "Status legend" toolbar button. The popover opens on hover or keyboard focus and closes on
 * mouse-leave, blur or Esc. Thresholds come from the ragBand constants so they cannot drift.
 */
export const StatusLegendButton = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const retentionDays = useSelector(selectRetentionDays);
  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <Wrapper>
      <TextButton
        type="button"
        data-testid="status-legend-button"
        aria-describedby={LegendId}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={(e) => {
          if (e.key === "Escape") hide();
        }}
      >
        <InfoIcon />
        Status legend
      </TextButton>
      <Popover id={LegendId} role="tooltip" hidden={!open} data-testid="status-legend">
        <h4>Status markers (compared with all rows on the same dependency key)</h4>
        <Row data-testid="status-legend-entry">
          <NoIconSwatch aria-hidden="true" />
          <span>
            <Name>No icon</Name> – for one of these reasons:
          </span>
          <ul>
            <li>
              <b>Not enough to compare:</b> fewer than {MinActiveRowsForRag} rows share the key.
            </li>
            <li>
              <b>{noCompletionsText(retentionDays, "the key")}:</b> everything is still waiting, which is normal
              before the data is published.
            </li>
            <li>
              <b>Progress unavailable:</b> the counts couldn&apos;t be loaded or the first completion time isn&apos;t known.
            </li>
          </ul>
        </Row>
        {Bands.map(({ band, name, meaning, when }) => (
          <Row key={band} data-testid="status-legend-entry">
            <RagMarker band={band} label={name} testId="legend-marker" />
            <span>
              <Name>{name}</Name> – {meaning}
            </span>
            <When>{when}</When>
          </Row>
        ))}
      </Popover>
    </Wrapper>
  );
};
